/*
 * Copyright 2025, IntraLinks, Inc. All rights reserved.
 */

import { useEffect } from 'react';
import { toReasonString } from './helpers.util';
import { DEFAULT_MIN_REQUIRED_LOA_LEVEL } from './constants.util';

const warn = (...args: any[]) => console.warn('[LoA:popup]', ...args);

/** SessionStorage flag used to mark that this popup has finished the LoA flow at least once. */
const SESSION_STORAGE_KEY = 'loa-flow-done';

/** Base BroadcastChannel name used by parent and popup to talk. */
export const LOA_CHANNEL = 'loa-upgrade';

const MODE_PARAM = 'mode';
const APP_ID_PARAM = 'applicationId';
const REL_PATH_PARAM = 'relativePath';

export type LoaLaunchMode = 'popup' | 'redirect';

export enum LoaMessageType {
  READY = 'LOA_READY',
  ELEVATED = 'LOA_ELEVATED',
  ABORTED = 'LOA_ABORTED',
  FAILED = 'LOA_FAILED',
}

type FlowSnapshot = {
  channelId: string | null;
  loaLevel: number | null;
  origin: string;
  mode: LoaLaunchMode | null;
  applicationId: string | null;
  relativePath: string | null;
  instance: string | null;
  resourceName: string | null;
};

function parseNum(value: string | null) {
  if (value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function safeDecode(value: string) {
  try { return decodeURIComponent(value); } catch { return value; }
}

const PROMPT_LABEL_MAX_LENGTH = 200;

function stripMarkupLikeContent(value: string): string {
  let sanitized = '';
  let insideMarkup = false;

  value.split('').forEach((char) => {
    if (char === '<') {
      insideMarkup = true;
      return;
    }

    if (char === '>') {
      insideMarkup = false;
      return;
    }

    if (!insideMarkup) {
      sanitized += char;
    }
  });

  return sanitized;
}

export function sanitizeResourceName(raw: string | null): string | null {
  if (raw == null) return null;
  const decoded = stripMarkupLikeContent(safeDecode(raw)).trim();
  if (!decoded) return null;
  return decoded.length > PROMPT_LABEL_MAX_LENGTH
    ? decoded.slice(0, PROMPT_LABEL_MAX_LENGTH)
    : decoded;
}

/** Whether the current page is inside the /securitysetup LoA flow routes. */
const isSecuritySetupRoute = /\/securitysetup(?:$|[?/])/.test(window.location.pathname);

let cachedFlow: FlowSnapshot | null = null;

/**
 * Capture URL params once at module init time so the rest of the file can reference
 * a stable snapshot (channelId, loaLevel) without repeatedly re-reading location/search.
 * If parsing fails we return a null snapshot and log (keeps the popup resilient).
 */
function computeFlowSnapshot(): FlowSnapshot {
  try {
    const query = new URLSearchParams(window.location.search);

    const rawMode = (query.get(MODE_PARAM) || '').toLowerCase();
    // Only recognise popup/redirect mode when we are on a /securitysetup route.
    let mode: LoaLaunchMode | null = null;
    if (isSecuritySetupRoute) {
      if (rawMode === 'redirect') mode = 'redirect';
      else if (rawMode === 'popup') mode = 'popup';
      else {
        mode = 'popup';
        warn(
          "Missing or invalid 'mode' param — defaulting to 'popup'.",
          'Integrators should pass mode=popup|redirect.'
        );
      }
    }

    let loaLevel = parseNum(query.get('loaLevel'));
    if (isSecuritySetupRoute && loaLevel == null) {
      loaLevel = DEFAULT_MIN_REQUIRED_LOA_LEVEL;
      warn(
        `Missing or invalid 'loaLevel' param — defaulting to ${DEFAULT_MIN_REQUIRED_LOA_LEVEL}.`
      );
    }

    const channelId = (query.get('channelId') || '').trim() || null;
    if (isSecuritySetupRoute && mode === 'popup' && !channelId) {
      warn(
        "Missing 'channelId' in popup mode —",
        'BroadcastChannel will use a shared name.',
        'Concurrent popups may conflict.'
      );
    }

    const applicationId = (query.get(APP_ID_PARAM) || '').trim() || null;
    const rawRelPath = (query.get(REL_PATH_PARAM) || '').trim() || null;
    const relativePath = rawRelPath ? safeDecode(rawRelPath) : null;

    return {
      channelId,
      loaLevel,
      origin: window.location.origin,
      mode,
      applicationId,
      relativePath,
      instance: (query.get('instance') || '').trim() || null,
      resourceName: sanitizeResourceName(query.get('resourceName'))
    };
  } catch (e) {
    warn('failed to parse flow snapshot', e);
    return {
      channelId: null,
      loaLevel: DEFAULT_MIN_REQUIRED_LOA_LEVEL,
      origin: window.location.origin,
      mode: isSecuritySetupRoute ? 'popup' : null,
      applicationId: null,
      relativePath: null,
      instance: null,
      resourceName: null
    };
  }
}

/** Getter so other modules don’t import FLOW directly (keeps the snapshot read-only).
 * lazily computes the snapshot on first access (no side effects at import)
*/
export const getFlowSnapshot = (): FlowSnapshot => {
  if (!cachedFlow) { cachedFlow = computeFlowSnapshot(); }
  return cachedFlow;
};

/** Read the current mode lazily (returns null when outside /securitysetup). */
function getMode(): LoaLaunchMode | null {
  return getFlowSnapshot().mode;
}

let cachedNavId: string | null = null;
/** Lazily generated per-navigation unique ID for BroadcastChannel correlation.
 * A per-navigation unique ID used to correlate messages from this specific popup
 * instance (or reload) so the parent can ignore stale messages from older navigations.
*/
function getNavId(): string {
  if (!cachedNavId) {
    try { cachedNavId = crypto.randomUUID(); } catch { cachedNavId = Math.random().toString(36).slice(2); }
  }
  return cachedNavId;
}

/** All message shapes exchanged over BroadcastChannel between popup and parent. */
export type LoaMessage =
  | { type: LoaMessageType.READY; navId: string; ts: number }
  | { type: LoaMessageType.ELEVATED; level: number; navId: string; ts: number }
  | { type: LoaMessageType.ABORTED; navId: string; ts: number }
  | { type: LoaMessageType.FAILED; reason?: string; navId: string; ts: number };

/** Build the BroadcastChannel name, optionally suffixing with channelId for isolation. */
function baseChannelName(): string {
  const id = getFlowSnapshot().channelId;
  return id ? `${LOA_CHANNEL}:${id}` : LOA_CHANNEL;
}

/**
 * In-memory mirror of the "done" flag to avoid repeated sessionStorage reads
 * and to let this module short-circuit quickly after completion.
 */
let doneMem = false;

/** One-time gate to avoid spamming logs if BroadcastChannel isn’t supported. */
let warnedNoBC = false;

/** Internal one-shot flag to ensure we emit LOA_READY only once per popup navigation. */
let readySent = false;

/**
 * Check if this popup has already completed the LoA flow in this browser session.
 *
 * Why sessionStorage?
 * - Survives *reloads* and *same-tab navigations* inside the popup.
 * - Is isolated per-popup window (doesn’t leak to other tabs).
 * - Lets us implement idempotent close/cleanup: if the page reloads after success/failure,
 *   we can tell we’re “already done” and immediately bail or close without re-running work.
 */
function isDone(): boolean {
  if (doneMem) return true;
  try {
    const v = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (v === '1') {
      doneMem = true;
      return true;
    }
  } catch (err) {
    warn('sessionStorage read failed', err);
  }
  return false;
}

export { isDone };

/**
 * Mark this popup as completed (both in memory and in sessionStorage).
 * Call this as soon as you know the outcome (success or failure) so any later
 * cleanup or reload logic can see the flag and avoid duplicate side effects.
 */
export function markPopupDone(): void {
  doneMem = true;
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, '1');
  } catch (err) {
    warn('sessionStorage write failed', err);
  }
}

/**
 * Low-level helper to publish a LoaMessage on the appropriate BroadcastChannel.
 * Creates the channel, posts, and immediately closes for least resource usage.
 */
function post(msg: LoaMessage): void {
  try {
    const name = baseChannelName();
    const ch = new BroadcastChannel(name);
    ch.postMessage(msg);
    ch.close();
  } catch (e) {
    if (!warnedNoBC) { warnedNoBC = true; warn('BroadcastChannel unavailable', e); }
  }
}

/**
 * Announces that this popup instance is loaded and ready to participate in the
 * BroadcastChannel protocol.
 *
 * When it runs:
 * - Only in `mode=popup`.
 * - Only once per popup navigation (guarded by `readySent`).
 *
 * Why:
 * - Parent uses LOA_READY to learn the current `navId` and ignore stale messages
 *   from older popup instances/reloads.
 * - We intentionally avoid posting READY at module-import time to prevent
 *   side effects when this module is imported outside the popup flow.
 */
export function announceReady(): void {
  if (readySent) return;
  if (getMode() !== 'popup') return;

  readySent = true;
  post({ type: LoaMessageType.READY, navId: getNavId(), ts: Date.now() });
}

/** Publish the “success” outcome with the elevated level to the parent. */
export function sendElevatedMsg(level: number): void {
  post({
    type: LoaMessageType.ELEVATED, level, navId: getNavId(), ts: Date.now()
  });
}

/** Publish the "failure" outcome (optional reason) to the parent. */
export function sendFailedMsg(reason?: unknown): void {
  post({
    type: LoaMessageType.FAILED,
    reason: reason == null ? undefined : toReasonString(reason),
    navId: getNavId(),
    ts: Date.now()
  });
}

/**
 * Heuristic to detect we’re a popup (opened via window.open or named pattern).
 * We only attempt programmatic close if it really looks like a popup.
 */
export function isLikelyPopup(): boolean {
  if (getMode() === 'popup') return true;
  let result = false;
  try { result = !!(window as any).opener || window.name === 'loa-elevation'; } catch { warn('cannot read opener/name'); }
  return result;
}

/**
 * Yield to the next animation frame (or resolve immediately if RAF is missing).
 * Used to give the browser a chance to flush posted messages/rendering before close().
 */
function nextFrame(): Promise<void> {
  return new Promise((resolve) => {
    if ('requestAnimationFrame' in window) {
      requestAnimationFrame(() => resolve());
    } else {
      resolve();
    }
  });
}

/**
 * Minimal flushing: drain microtasks and yield one frame before closing.
 * This reduces the likelihood of messages/logs being dropped by an immediate close().
 */
async function flushBeforeClose(): Promise<void> {
  await Promise.resolve();
  await nextFrame();
}

/**
 * Idempotent close of the popup window:
 * - Only runs if we look like a popup.
 * - Ensures the "done" flag is set (defensive in case caller forgot to mark).
 * - Flushes a tick before attempting close to improve delivery of final messages.
 * - Tries alternate close strategies for stricter browsers.
 */
export async function closeNow(): Promise<void> {
  if (!isLikelyPopup()) return;
  if (!isDone()) markPopupDone();
  await flushBeforeClose();
  try { window.close(); } catch {
    try { window.open('', '_self')?.close(); } catch { warn('cannot close the popup'); }
  }
}

/**
 * Tracks whether a navigation to an external LoA provider is intentionally in progress.
 * When true, popup lifecycle handlers (e.g., beforeunload/pagehide) must not emit
 * an "ABORTED" message, because navigation is part of the normal flow for non-SSO
 * users (not an unexpected interruption by the user).
 * This flag is set immediately before performing the external redirect.
 */
let externalRedirectInProgress = false;

/**
 * Marks that the popup is intentionally navigating away (e.g., to an external LoA provider).
 * Used to suppress accidental ABORTED events during controlled navigation.
 */
export function markExternalRedirectStarted(): void {
  externalRedirectInProgress = true;
}

/**
 * Boolean helper to check if we're in a valid external navigation phase.
 * If true, popup abort flows must not trigger.
 */
function isExternalRedirectInProgress(): boolean {
  return externalRedirectInProgress;
}

export function markElevationSuccess(level?: number): void {
  markPopupDone();
  if (getMode() === 'popup' && typeof level === 'number') {
    sendElevatedMsg(level);
  }
}

export function markElevationError(reason?: unknown): void {
  markPopupDone();
  if (getMode() === 'popup') {
    sendFailedMsg(reason);
  }
}

/**
 * React hook that wires “ABORTED” notifications on page exit/navigation.
 * Parent debounces this event (ABORT_DEBOUNCE_MS) to avoid false positives
 * during quick intra-page transitions or immediate close after success/failure.
 */
export function usePopupChannels(): void {
  useEffect(() => {
    if (getMode() !== 'popup') {
      return undefined;
    };

    announceReady();

    let sent = false;
    const sendAbortOnce = () => {
      if (sent) return;
      sent = true;

      if (isDone() || isExternalRedirectInProgress()) {
        return;
      }
      post({ type: LoaMessageType.ABORTED, navId: getNavId(), ts: Date.now() });
    };

    window.addEventListener('beforeunload', sendAbortOnce);
    window.addEventListener('pagehide', sendAbortOnce);
    return () => {
      window.removeEventListener('beforeunload', sendAbortOnce);
      window.removeEventListener('pagehide', sendAbortOnce);
    };
  }, []);
}
